import { nextTick } from "vue";
import { createI18n } from "vue-i18n";

import { defaultLocale } from "shared/boot/locale";
import en from "shared/i18n/en_AU.json";
import StorageService from "shared/services/StorageService";

let i18n;

export function getI18n() {
  if (!i18n) {
    i18n = createI18n({
      allowComposition: true,
      fallbackLocale: "en-AU",
      fallbackWarn: false,
      globalInjection: true,
      legacy: true,
      locale: defaultLocale(),
      messages: {
        "en-AU": en,
      },
      numberFormats: {
        de: {
          currency: {
            currency: "EUR",
            minimumFractionDigits: 0,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
        en: {
          currency: {
            currency: "AUD",
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
        "en-AU": {
          currency: {
            currency: "AUD",
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
        "en-CA": {
          currency: {
            currency: "CAD",
            minimumFractionDigits: 0,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
        "en-GB": {
          currency: {
            currency: "GBP",
            minimumFractionDigits: 0,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
        "en-US": {
          currency: {
            currency: "USD",
            minimumFractionDigits: 0,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
        "fr-CA": {
          currency: {
            currency: "CAD",
            minimumFractionDigits: 0,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
        "zh-CN": {
          currency: {
            currency: "CNY",
            minimumFractionDigits: 0,
            style: "currency",
          },
          percentage: {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          },
          suffix: {
            compactDisplay: "short",
            notation: "compact",
          },
        },
      },
      warnHtmlMessage: false,
    });
  }

  return i18n;
}

export const SUPPORTED_LOCALES = [
  "en-AU",
  "en-CA",
  "en-GB",
  "en-US",
  "en",
  "es",
  "fr-CA",
  "fr",
  "it",
  "zh-CN",
  "de",
];

export function getLocale() {
  return getI18n().global.locale;
}

export function getLocaleDate(date, options) {
  return getI18n().global.d(date, options);
}

/**
 * @param {number} number
 * @param {string | object} [option]
 * @param {string | null} [locale]
 */
export function getLocaleNumber(number, option, locale = null) {
  return getI18n().global.n(number, option, locale);
}

export function getLocalePluralText(key, count, values = {}) {
  return getI18n().global.t(key, { count, ...values });
}

/**
 * @param {string} key
 * @param {Object} [values]
 * @returns {string}
 */
export function getLocaleText(key, values) {
  return getI18n().global.t(key, values);
}

export function getLocaleValue(key) {
  return getI18n().global.tm(key);
}

export async function loadLocaleMessages(locale) {
  const messages = (await import(`../i18n/${locale.replace("-", "_")}.json`))
    .default;

  getI18n().global.setLocaleMessage(locale, messages);

  return nextTick();
}

export async function setLocale(newLocale) {
  const locale = newLocale || defaultLocale();
  const baseLocale = locale.split("-")[0];
  const locales = [locale, baseLocale];

  const targetLocale = SUPPORTED_LOCALES.find((supportedLocale) =>
    locales.includes(supportedLocale)
  );

  if (targetLocale) {
    if (!getI18n().global.availableLocales.includes(targetLocale)) {
      await loadLocaleMessages(targetLocale).catch(() => {});
    }

    getI18n().global.locale = targetLocale;
  }
}

export default async ({ app }) => {
  app.use(getI18n());

  await setLocale(await StorageService.get("locale"));
};
